import axios from 'axios';
import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';
import React from 'react';

import {
  FIELDS_WITH_FILTERS,
  FIELDS_WITH_CONFIG,
  FIELDS_WITH_TEXT,
  FIRST_COST_CODE_KEY,
  TIME_TRACKING_GRID_EXPORT_KEYS,
  TIME_TRACKING_GRID_HOUR_KEYS,
} from '../../settings/Exports/exports.constants';
import { fixedDecimalKeys } from './exports.constants';
import { request } from '../../helpers/requests';
import { includesTerm } from '../../helpers/helpers';

const getTimeTrackingGridSubKey = (field = '') => {
  const parts = field?.split('-') ?? [];
  if (parts.length < 3) return null;
  return parts.slice(2).join('-');
};

export const getTimeTrackingGridFieldId = (columns) => (
  columns.reduce((acc, { field } = {}) => {
    if (acc) return acc;
    const subKey = getTimeTrackingGridSubKey(field);
    if (!subKey || !TIME_TRACKING_GRID_EXPORT_KEYS.has(subKey)) return acc;
    const parts = field.split('-');
    return parts.slice(0, 2).join('-');
  }, null)
);

export const exportHasTimeTrackingGridColumns = (columns = []) => (
  !!getTimeTrackingGridFieldId(columns)
);

export const isTimeTrackingGridHourField = (field = '') => {
  const subKey = getTimeTrackingGridSubKey(field);
  return !!subKey && TIME_TRACKING_GRID_HOUR_KEYS.has(subKey);
};

const TIME_ENTRY_DATE_FIELDS = new Set(['date', 'startTime', 'endTime']);

const NUMERIC_BUILTIN_EXPORT_FIELDS = new Set([
  'totalHours', 'workHours', 'breakHours', 'regularHours', 'otHours', 'doubleOT', 'ptoHours',
  'calculatedWage', 'cost', 'burdenCost', 'users.wage',
  'costcodes.hours', 'costcodes.hourlywage', 'class.hourlywage',
]);

const DECIMAL_EXPORT_FIELD_IDS = new Set([
  ...fixedDecimalKeys,
  ...NUMERIC_BUILTIN_EXPORT_FIELDS,
]);

const getColumnFieldIdForExport = (col = {}) => {
  if (col.isMulti && col.fields?.length) {
    return col.fields[0].replace(/-(lat|lng)$/i, '');
  }
  return col.field;
};

export const isExportDateTimeColumn = (col) => {
  if (!col) return false;
  const field = col.field || '';
  return (
    col.isCustomDateField
    || col.isDateField
    || field.startsWith('exportDate')
    || TIME_ENTRY_DATE_FIELDS.has(field)
    || (field === 'createdAt' || field === 'lastUpdated')
  );
};

export const isExportDecimalColumn = (col, fieldMap = {}) => {
  const fieldId = getColumnFieldIdForExport(col);
  if (DECIMAL_EXPORT_FIELD_IDS.has(fieldId)) return true;
  return fieldMap[fieldId]?.selectedType === 'calculation';
};

export const isExportNumericLikeColumn = (col, fieldMap = {}) => {
  const fieldId = getColumnFieldIdForExport(col);
  if (NUMERIC_BUILTIN_EXPORT_FIELDS.has(fieldId)) return true;
  if (isTimeTrackingGridHourField(fieldId)) return true;
  return fieldMap[fieldId]?.selectedType === 'calculation';
};

export const isExportTextOrAttributeColumn = (col, fieldMap = {}) => {
  if (FIELDS_WITH_TEXT.has(col.field)) return true;
  const fieldId = getColumnFieldIdForExport(col);
  const selectedType = fieldMap[fieldId]?.selectedType;
  return selectedType === 'text' || selectedType === 'attribute';
};

export const rowMatchesExportColCombinedFilter = (cellStr, { values, text }) => {
  if (text && !includesTerm(cellStr, text)) return false;
  if (values?.length) return values.some((e) => cellStr === e);
  return true;
};

const extractNumberValueFromCell = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  // Remove commas, currency, and percentage symbols
  const stripped = raw.replace(/[,$\s]/g, '').replace(/%/g, '');
  const parsedFloat = parseFloat(stripped);
  return Number.isNaN(parsedFloat) ? null : parsedFloat;
};

export const rowMatchesExportNumberFilter = (rawCell, { min, max, searchText }) => {
  const text = searchText
    ? searchText.trim()
    : null;
  const cellStr = rawCell == null ? '' : rawCell;
  if (text && !includesTerm(cellStr, text)) return false;

  const hasRange = min != null || max != null;
  if (!hasRange) return Boolean(text);

  const n = extractNumberValueFromCell(rawCell);
  if (n == null) return false;
  if (min != null && n < min) return false;
  if (max != null && n > max) return false;
  return true;
};

export const getDataKey = (col = {}) => {
  const {
    field,
    fields,
    isMulti,
    filterValue,
    id: colId,
  } = col;
  if (isMulti) return fields.join(':');
  if (!filterValue) return field;

  if (FIELDS_WITH_FILTERS.has(col.field) || FIELDS_WITH_CONFIG.has(col.field)) {
    return `${field}-${filterValue}`;
  }
  if (FIELDS_WITH_TEXT.has(col.field)) {
    return colId;
  }
  return field;
};

export const getTableFieldId = (columns) => (
  columns.reduce((acc, { field } = {}) => {
    if (!field) return acc;
    const fieldSplit = field.split('-');
    if (fieldSplit.length < 3) return acc;
    if (fieldSplit[2] === FIRST_COST_CODE_KEY) return acc;
    return fieldSplit.slice(0, 2).join('-');
  }, null)
);

export const updateFormNumbers = ({ data = [], formNumberMap = {} }) => {
  const newData = data.map(({ id, ...form }) => {
    const {
      [id]: number,
    } = formNumberMap;

    return {
      ...form,
      id,
      number,
    };
  });

  return newData;
};

export const getBatchNumber = async ({ exportId }) => {
  const { data: newBatchNumber } = await request({
    call: axios.get(`/exports/${exportId}/batchNumber`),
    hideSuccessToast: true,
  });
  if (newBatchNumber) {
    return newBatchNumber;
  }
  Sentry.withScope(() => {
    Sentry.captureException(new Error('Failed to grab batch number during export'));
  });
  return null;
};

export const alterAfterExportFormPayload = ({
  payload = {},
  rows = [],
  generateFormNumber,
}) => {
  const payloadRef = payload;
  payloadRef.generateFormNumber = !!generateFormNumber;
  payloadRef.formIds = Array.from(rows.reduce((acc, datum) => {
    const { id: formId } = datum;
    /*
      If there is a table we append -{index} to give each row
      a unique key.
    */
    const [prefix] = formId?.split?.('-') ?? [];
    if (prefix) acc.add(prefix);
    return acc;
  }, new Set()));
};

export const alterAfterExportTaskPayload = ({
  payload = {},
  rows = [],
}) => {
  const payloadRef = payload;
  payloadRef.taskIds = Array.from(rows.reduce((acc, datum) => {
    const { id, taskId } = datum;
    const [prefix] = id?.split?.('-') ?? [];
    acc.add(taskId ?? prefix);
    return acc;
  }, new Set()));
};

export const constructAfterExportPayload = ({
  type,
  rows = [],
  generateFormNumber = false,
  batchNumber,
}) => {
  if (!type) return {};

  const payload = {};

  if (batchNumber) payload.number = batchNumber;

  if (type === 'forms') {
    alterAfterExportFormPayload({
      payload,
      generateFormNumber,
      rows,
    });
  }
  if (type === 'timeEntries') {
    alterAfterExportTaskPayload({
      payload,
      rows,
    });
  }

  return payload;
};

const parseTimeOnlyDisplayString = (raw) => {
  if (!raw) return null;

  const t = DateTime.fromFormat(raw, 'h:mm a', { locale: 'en' });
  if (!t.isValid) return null;

  return DateTime.fromObject(
    {
      hour: t.hour,
      minute: t.minute,
      second: t.second,
      millisecond: t.millisecond,
    },
    { zone: DateTime.local().zone },
  );
};

const dateFormatsToCheck = [
  'LL/dd/yyyy',
  'L/d/yyyy',
];

export const parseDateTime = (raw, timeOfDayOnly = false) => {
  if (raw == null) return null;
  if (typeof raw === 'number') {
    return DateTime.fromMillis(raw);
  }

  let parsedDT = DateTime.fromISO(raw);

  if (timeOfDayOnly) {
    const fromTimeOnly = parseTimeOnlyDisplayString(raw);
    if (fromTimeOnly) {
      parsedDT = fromTimeOnly;
    }

    return parsedDT.isValid ? parsedDT : null;
  }

  if (typeof raw === 'string') {
    if (!parsedDT.isValid) {
      const norm = raw.replace(/a\.m\./gi, 'AM').replace(/p\.m\./gi, 'PM');
      parsedDT = DateTime.fromFormat(norm, 'LLL d, yyyy h:mm a', { locale: 'en' });
    }

    dateFormatsToCheck.forEach((format) => {
      const parsed = DateTime.fromFormat(raw.trim(), format, { locale: 'en' });
      if (parsed.isValid && !parsedDT.isValid) {
        parsedDT = parsed;
      }
    });

    return parsedDT.isValid ? parsedDT : null;
  }

  if (raw instanceof Date) {
    return DateTime.fromJSDate(raw);
  }
  return null;
};

const parseCellDateTime = (record, col, dataKey, timeOfDayOnly = false) => {
  if (record[col.field] == null) return null;
  if (col?.isCustomDateField) {
    const fromField = parseDateTime(record[col.field], timeOfDayOnly);
    if (fromField?.isValid) return fromField;
  }

  const fromDataKey = parseDateTime(record[dataKey], timeOfDayOnly);
  if (fromDataKey?.isValid) return fromDataKey;
  return parseDateTime(record[col.field]);
};

const dateTimeToMS = (dt) => {
  if (!dt?.isValid) return null;
  const local = dt.setZone(DateTime.local().zone);
  return local.hour * 3600000
    + local.minute * 60000
    + local.second * 1000
    + local.millisecond;
};

export const extractTextFromCell = (value) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (React.isValidElement(value)) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return value;
  }
};

export const rowMatchesExportDateFilter = (record, col, dataKey, filterVal) => {
  if (!filterVal) return false;
  const hasText = Boolean(filterVal.searchText?.trim());
  if (!filterVal.after && !filterVal.before && !hasText) return false;

  const dt = parseCellDateTime(record, col, dataKey, filterVal.timeOfDayOnly);
  if (!dt?.isValid) return false;

  const ms = filterVal.timeOfDayOnly ? dateTimeToMS(dt) : dt.toMillis();
  if (filterVal.after != null && ms < filterVal.after) return false;
  if (filterVal.before != null && ms > filterVal.before) return false;

  if (hasText) {
    const cell = extractTextFromCell(record[dataKey]);
    if (!includesTerm(cell, filterVal.searchText.trim())) return false;
  }
  return true;
};

const tableFilterValueIsActive = (val) => {
  if (val == null) return false;
  if (Array.isArray(val)) return val.length > 0;
  return true;
};

export const applyExportTableFilters = ({
  rows,
  tableFilters,
  filteredData,
}) => {
  if (!tableFilters) return rows;
  const hasActiveColumnFilter = Object.values(tableFilters).some(tableFilterValueIsActive);
  if (!hasActiveColumnFilter) return rows;
  return filteredData ?? rows;
};

export const SUBTOTAL_NOT_AVAILABLE = 'Not Available';

const subTotalNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const subTotalDecimalFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const subTotalCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/**
 * Computes the sub-total for a single export column over the provided rows.
 * - Sums every non-empty cell that parses to a number.
 * - If any non-empty cell is non-numeric (or there are no numeric cells),
 *   returns SUBTOTAL_NOT_AVAILABLE.
 * - Formats as currency when any cell contains a currency symbol.
 * - Formats with two decimal places for decimal / calculation columns.
 */
export const getColumnSubTotal = (col, rows = [], fieldMap = {}) => {
  if (!col) return SUBTOTAL_NOT_AVAILABLE;
  const dataKey = getDataKey(col);
  let hasCurrency = false;
  let numericCount = 0;
  let sum = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const cellStr = extractTextFromCell(rows[i]?.[dataKey]);
    if (cellStr !== '' && cellStr != null) {
      const cellType = typeof cellStr;
      if (cellType !== 'number' && cellType !== 'string') {
        return SUBTOTAL_NOT_AVAILABLE;
      }
      if (cellType === 'string' && cellStr.includes('$')) hasCurrency = true;
      // Strip currency, thousands separators, percent signs and whitespace
      const stripped = typeof cellStr === 'number'
        ? cellStr
        : cellStr.replace(/[$,%\s]/g, '');
      const num = Number(stripped);
      if (stripped === '' || Number.isNaN(num)) return SUBTOTAL_NOT_AVAILABLE;
      sum += num;
      numericCount += 1;
    }
  }

  if (numericCount === 0) return SUBTOTAL_NOT_AVAILABLE;
  if (hasCurrency) return subTotalCurrencyFormatter.format(sum);
  if (isExportDecimalColumn(col, fieldMap)) return subTotalDecimalFormatter.format(sum);
  return subTotalNumberFormatter.format(sum);
};
